import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import "../styles/Dashboard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBillTransfer,
  faCreditCard,
  faMoneyCheck,
  faUserCog,
  faList,
  faBullhorn,
} from "@fortawesome/free-solid-svg-icons";
import MyTransactions from "../components/dashboard/MyTransactions";
import Promotions from "../components/dashboard/Promotions";
import Loader from "../components/Loader";

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [savingsBalance, setSavingsBalance] = useState(0);
  const [loansBalance, setLoansBalance] = useState(0);
  const [investmentsBalance, setInvestmentsBalance] = useState(0);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBalances = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          const transactions = data.transactions || [];

          const calculateBalance = (accountType) => {
            return transactions
              .filter((t) => t.account === accountType)
              .reduce((total, t) => {
                return t.type === "credit"
                  ? total + t.amount
                  : total - t.amount;
              }, 0);
          };

          setSavingsBalance(calculateBalance("Savings"));
          setLoansBalance(calculateBalance("Loans"));
          setInvestmentsBalance(calculateBalance("Investments"));
        }
      } catch (error) {
        console.error("Error fetching balances:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBalances();
  }, [user]);

  const totalBalance = savingsBalance + loansBalance + investmentsBalance;
  const fullName = userData?.fullName || "User";
  const accountNumber = userData?.accountNumber || "";
  const userRole = userData?.role || "";

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="dashboard-page">
      {/* Header greeting section */}
      <div className="dashboard-header">
        <div className="header-greeting">Hello {fullName}</div>
        <div className="header-balance">
          Account Balance: $
          {totalBalance.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      </div>

      {/* Balance Card and Responsive Carousel */}
      <div className="dashboard-grid">
        <div className="balance-box">
          <div className="balance-content">
            <div className="balance-labels">Savings</div>
            <div className="balance-amount">
              $
              {savingsBalance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="balance-footer">Account No: {accountNumber}</div>
        </div>

        <div className="balance-box">
          <div className="balance-content">
            <div className="balance-labels">Loans</div>
            <div className="balance-amount">
              $
              {loansBalance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="balance-footer">
            {loansBalance === 0 ? "No loans" : "View loans"}
          </div>
        </div>

        <div className="balance-box">
          <div className="balance-content">
            <div className="balance-labels">Investments</div>
            <div className="balance-amount">
              $
              {investmentsBalance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="balance-footer">
            {userRole === "investor"
              ? "Active Investment"
              : "No active investment"}
          </div>
        </div>
      </div>

      {/* Quick navigation */}
      <div className="click-row">
        <span className="click-box" onClick={() => navigate("/transfer")}>
          <FontAwesomeIcon icon={faMoneyBillTransfer} />
          <span>Transfer</span>
        </span>
        <span className="click-box" onClick={() => navigate("/my-card")}>
          <FontAwesomeIcon icon={faCreditCard} />
          <span>My Card</span>
        </span>
        <span className="click-box" onClick={() => navigate("/loans")}>
          <FontAwesomeIcon icon={faMoneyCheck} />
          <span>Loans</span>
        </span>
        <span
          className="click-box"
          onClick={() => navigate(`/user/${user?.uid}`)}
        >
          <FontAwesomeIcon icon={faUserCog} />
          <span>Settings</span>
        </span>
      </div>

      {/* Transaction and promotion section */}
      <div className="dash-info">
        {/* Transaction section */}
        <div className="dash-info-box">
          <div className="dash-info-header">
            <FontAwesomeIcon icon={faList} />
            <span>Transactions</span>
          </div>
          <MyTransactions user={user} limit={3} showViewAll={true} />
        </div>
        {/* Promotion section */}
        <div className="dash-info-box">
          <div className="dash-info-header">
            <FontAwesomeIcon icon={faBullhorn} />
            <span>Promotion</span>
          </div>
          <Promotions />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
